import { Link } from "react-router-dom";
import { Building2, ChevronRight, ShieldCheck, TrendingUp } from "lucide-react";
import { Badge } from "../lib/ui";
import { MetricStrip } from "./SubFlow";
import { programStepHref } from "../lib/programFlows";

/** Per-program modernization analytics — high level before step drill-down. */
export default function ProgramAnalytics({ programId, data, programMeta }) {
  const core = data?.core || {};
  const counts = core.counts || programMeta?.summary || {};
  const health = core.health || {};
  const dashboard = data?.dashboard || {};
  const record = core.record || programMeta || {};

  const tiles = [
    {
      module: "projects",
      testId: "program-kpi-projects",
      icon: Building2,
      tone: "blue",
      label: "Projects",
      value: counts.projects ?? counts.applications ?? 0,
      detail: `${counts.no_project ?? 0} apps without project`,
    },
    {
      module: "execution",
      testId: "program-kpi-waves",
      icon: ShieldCheck,
      tone: "cyan",
      label: "In execution",
      value: counts.in_execution ?? 0,
      detail: `${counts.approved ?? 0} MDR sealed`,
    },
    {
      module: "govern",
      testId: "program-kpi-govern",
      icon: TrendingUp,
      tone: "green",
      label: "Assessed",
      value: counts.assessed ?? 0,
      detail: `Health ${health.score ?? "—"} ${health.rag ?? ""}`.trim(),
    },
  ];

  return (
    <section className="analytics-overview program-analytics" data-testid="program-analytics">
      <MetricStrip
        items={[
          { label: "Applications", value: counts.applications ?? "—", testId: "program-metric-apps" },
          { label: "Approved MDR", value: counts.approved ?? "—", testId: "program-metric-mdr" },
          { label: "Completed", value: counts.completed ?? "—", testId: "program-metric-done" },
          {
            label: "Health",
            value: health.score != null ? `${health.score}` : "—",
            detail: health.rag?.toUpperCase(),
            testId: "program-metric-health",
          },
        ]}
      />

      {dashboard?.note && <p className="admin-note" data-testid="program-scale-note">{dashboard.note}</p>}

      <div className="analytics-summary metric-grid">
        {tiles.map(({ module, testId, icon: Icon, tone, label, value, detail }) => (
          <Link
            key={module}
            to={programStepHref(programId, module)}
            className={`metric metric-drill tone-${tone}`}
            data-testid={testId}
          >
            <span className={`metric-icon ${tone}`}><Icon size={18} /></span>
            <div>
              <small>{label}</small>
              <strong>{value}</strong>
              <span>{detail}</span>
            </div>
            <ChevronRight size={16} className="metric-chevron" />
          </Link>
        ))}
      </div>

      <div className="feature-panel analytics-panel" data-testid="program-analytics-context">
        <div className="panel-header compact">
          <div>
            <p className="kicker">PROGRAM · {record.id || programId}</p>
            <h2>{record.name || "Programme"}</h2>
          </div>
          <Badge tone={health.rag === "red" ? "rose" : health.rag === "amber" ? "amber" : "green"}>
            {health.rag?.toUpperCase() || "RAG"}
          </Badge>
        </div>
        <p className="muted small-note">{record.description || record.business_objective}</p>
        <p className="muted small-note">
          Sponsor {record.executive_sponsor || "—"} · PM {record.program_manager || "—"} · {record.lifecycle || record.status}
        </p>
        {core.red_risks_open > 0 && (
          <p className="muted small-note" data-testid="program-red-risks">
            {core.red_risks_open} open RED risk{core.red_risks_open === 1 ? "" : "s"}
          </p>
        )}
      </div>
    </section>
  );
}
